import React from "react";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import ModalClose from "@mui/joy/ModalClose";
import Typography from "@mui/joy/Typography";
import Divider from "@mui/joy/Divider";
import Sheet from "@mui/joy/Sheet";
import Stack from "@mui/joy/Stack";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Input from "@mui/joy/Input";
import Select from "@mui/joy/Select";
import Option from "@mui/joy/Option";
import Checkbox from "@mui/joy/Checkbox";
import Button from "@mui/joy/Button";
import CircularProgress from "@mui/joy/CircularProgress";

import { AdminGameFilterInfo } from "../../models/gameFilters";
import { computeAvailabilityFlags, splitAvailabilityFlags, toNumber } from "./gameFiltersUtils";

type Props = {
    open: boolean;
    setOpen: (open: boolean) => void;
    filters: AdminGameFilterInfo[];
    onConfirm: (updated: AdminGameFilterInfo[]) => void;
    isSaving: boolean;
};

export const GameFilterGroupEditorDialog: React.FC<Props> = ({ open, setOpen, filters, onConfirm, isSaving }) => {
    const sample = filters[0];

    const [groupNameRu, setGroupNameRu] = React.useState(sample.groupNameRu);
    const [groupNameEn, setGroupNameEn] = React.useState(sample.groupNameEn);
    const [filterType, setFilterType] = React.useState<number>(toNumber(sample.filterType, 0));
    const [filterRegion, setFilterRegion] = React.useState<number>(toNumber(sample.filterRegion, 0));
    const [colorType, setColorType] = React.useState<number>(sample.colorType == null ? -1 : toNumber(sample.colorType, 0));
    const [groupOrder, setGroupOrder] = React.useState<string>(String(sample.groupOrder));

    const initialAvail = splitAvailabilityFlags(sample.availability);
    const [availBuyPage, setAvailBuyPage] = React.useState<boolean>(initialAvail.buy);
    const [availPendingPage, setAvailPendingPage] = React.useState<boolean>(initialAvail.pending);
    const [availInventoryPage, setAvailInventoryPage] = React.useState<boolean>(initialAvail.inventory);
    const [availSalePage, setAvailSalePage] = React.useState<boolean>(initialAvail.sale);
    const [availPlaceBidPage, setAvailPlaceBidPage] = React.useState<boolean>(initialAvail.placeBid);
    const [availMyOrdersPage, setAvailMyOrdersPage] = React.useState<boolean>(initialAvail.myOrders);
    const [availCreateOrder, setAvailCreateOrder] = React.useState<boolean>(initialAvail.createOrder);
    const [availProfile, setAvailProfile] = React.useState<boolean>(initialAvail.profile);
    const [availAllBidsPage, setAvailAllBidsPage] = React.useState<boolean>(initialAvail.allBids);
    const [availSkinPage, setAvailSkinPage] = React.useState<boolean>(initialAvail.skin);

    React.useEffect(() => {
        const s = filters[0];
        setGroupNameRu(s.groupNameRu);
        setGroupNameEn(s.groupNameEn);
        setFilterType(toNumber(s.filterType, 0));
        setFilterRegion(toNumber(s.filterRegion, 0));
        setColorType(s.colorType == null ? -1 : toNumber(s.colorType, 0));
        setGroupOrder(String(s.groupOrder));

        const avail = splitAvailabilityFlags(s.availability);
        setAvailBuyPage(avail.buy);
        setAvailPendingPage(avail.pending);
        setAvailInventoryPage(avail.inventory);
        setAvailSalePage(avail.sale);
        setAvailPlaceBidPage(avail.placeBid);
        setAvailMyOrdersPage(avail.myOrders);
        setAvailCreateOrder(avail.createOrder);
        setAvailProfile(avail.profile);
        setAvailAllBidsPage(avail.allBids);
        setAvailSkinPage(avail.skin);
    }, [filters]);

    const handleSave = () => {
        const avail = computeAvailabilityFlags(
            availBuyPage,
            availPendingPage,
            availInventoryPage,
            availSalePage,
            availPlaceBidPage,
            availMyOrdersPage,
            availCreateOrder,
            availProfile,
            availAllBidsPage,
            availSkinPage,
        );

        const next = filters.map(f => {
            const updated: AdminGameFilterInfo = {
                ...f,
                groupNameRu,
                groupNameEn,
                filterType,
                filterRegion,
                availability: avail,
                groupOrder: Number.isFinite(Number(groupOrder)) ? Number(groupOrder) : f.groupOrder,
            };
            if (colorType === -1) {
                updated.colorType = undefined;
            } else {
                updated.colorType = colorType as any;
            }
            return updated;
        });

        onConfirm(next);
    };

    return (
        <Modal open={open} onClose={() => { if (!isSaving) setOpen(false); }}>
            <ModalDialog
                sx={{
                    maxWidth: 720,
                    width: { xs: 'calc(100% - 32px)', sm: 720 },
                    mx: 'auto',
                    my: { xs: 2, sm: 4 },
                    maxHeight: '90vh',
                    overflow: 'auto',
                }}
            >
                <ModalClose/>
                <Typography level="h2">
                    Редактировать группу ({sample.groupNameRu} / {sample.groupNameEn})
                </Typography>
                <Divider sx={{ my: 2 }}/>
                <Sheet sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Группа (RU)</FormLabel>
                            <Input value={groupNameRu} onChange={(e) => setGroupNameRu(e.target.value)} />
                        </FormControl>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Группа (EN)</FormLabel>
                            <Input value={groupNameEn} onChange={(e) => setGroupNameEn(e.target.value)} />
                        </FormControl>
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Сортировка группы</FormLabel>
                            <Input value={groupOrder} onChange={(e) => setGroupOrder(e.target.value)} />
                        </FormControl>
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <FormControl sx={{ flex: 1, minWidth: 0 }}>
                            <FormLabel>Тип отображения фильтра</FormLabel>
                            <Select
                                value={filterType}
                                onChange={(_, v) => setFilterType((v as number) ?? 0)}
                                sx={{ width: '100%' }}
                                slotProps={{
                                    listbox: {
                                        sx: {
                                            maxHeight: 260,
                                            overflow: 'auto',
                                        },
                                    },
                                }}
                            >
                                <Option value={0}>CheckBox (можно выбрать несколько элементов)</Option>
                                <Option value={1}>Range (от-до, например цена)</Option>
                                <Option value={2}>Select (список с одним выбранным элементом)</Option>
                                <Option value={3}>Grid (таблица с одним выбранным элементом)</Option>
                                <Option value={4}>RadioButton (один элемент)</Option>
                            </Select>
                        </FormControl>
                        <FormControl sx={{ flex: 1, minWidth: 0 }}>
                            <FormLabel>Регион отображения</FormLabel>
                            <Select
                                value={filterRegion}
                                onChange={(_, v) => setFilterRegion((v as number) ?? 0)}
                                sx={{ width: '100%' }}
                                slotProps={{
                                    listbox: {
                                        sx: {
                                            maxHeight: 260,
                                            overflow: 'auto',
                                        },
                                    },
                                }}
                            >
                                <Option value={0}>Верхнее меню</Option>
                                <Option value={1}>Боковое</Option>
                            </Select>
                        </FormControl>
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <FormControl sx={{ flex: 1, minWidth: 0 }}>
                            <FormLabel>Форма индикатора цвета</FormLabel>
                            <Select
                                value={colorType}
                                onChange={(_, v) => setColorType((v as number) ?? 0)}
                                sx={{ width: '100%' }}
                                slotProps={{
                                    listbox: {
                                        sx: {
                                            maxHeight: 260,
                                            overflow: 'auto',
                                        },
                                    },
                                }}
                            >
                                <Option value={-1}>Без цвета заливки</Option>
                                <Option value={0}>Квадрат</Option>
                                <Option value={1}>Круг</Option>
                                <Option value={2}>Звезда</Option>
                            </Select>
                        </FormControl>
                    </Stack>

                    <div>
                        <FormLabel>Где доступно</FormLabel>
                        <Stack
                            sx={{
                                mt: 0.5,
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
                                gap: 2,
                                alignItems: "start",
                            }}
                        >
                            <Checkbox
                                label="Купить"
                                checked={availBuyPage}
                                onChange={(e) => setAvailBuyPage(e.target.checked)}
                            />
                            <Checkbox
                                label="Ожидание"
                                checked={availPendingPage}
                                onChange={(e) => setAvailPendingPage(e.target.checked)}
                            />
                            <Checkbox
                                label="Инвентарь"
                                checked={availInventoryPage}
                                onChange={(e) => setAvailInventoryPage(e.target.checked)}
                            />
                            <Checkbox
                                label="Продажи"
                                checked={availSalePage}
                                onChange={(e) => setAvailSalePage(e.target.checked)}
                            />
                            <Checkbox
                                label="Разместить заявку"
                                checked={availPlaceBidPage}
                                onChange={(e) => setAvailPlaceBidPage(e.target.checked)}
                            />
                            <Checkbox
                                label="Все заявки"
                                checked={availAllBidsPage}
                                onChange={(e) => setAvailAllBidsPage(e.target.checked)}
                            />
                            <Checkbox
                                label="Мои заявки"
                                checked={availMyOrdersPage}
                                onChange={(e) => setAvailMyOrdersPage(e.target.checked)}
                            />
                            <Checkbox
                                label="Создания заявки на покупку"
                                checked={availCreateOrder}
                                onChange={(e) => setAvailCreateOrder(e.target.checked)}
                            />
                            <Checkbox
                                label="Публичный профиль"
                                checked={availProfile}
                                onChange={(e) => setAvailProfile(e.target.checked)}
                            />
                            <Checkbox
                                label="Страница товара"
                                checked={availSkinPage}
                                onChange={(e) => setAvailSkinPage(e.target.checked)}
                            />
                        </Stack>
                    </div>

                    {isSaving ? (
                        <Stack direction="row" spacing={1} sx={{ justifyContent: 'center', mt: 1 }}>
                            <CircularProgress size="md" />
                        </Stack>
                    ) : (
                        <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end', mt: 1 }}>
                            <Button color="neutral" onClick={() => setOpen(false)}>
                                Отмена
                            </Button>
                            <Button color="primary" onClick={handleSave}>
                                Сохранить
                            </Button>
                        </Stack>
                    )}
                </Sheet>
            </ModalDialog>
        </Modal>
    );
};

